import {TrackingPromotionCreative} from '@aboutyou/legacy-tracking-sdk';
import React from 'react';
import styled, {css} from 'styled-components';
import {LiveShoppingEvent} from '../../../../../grpc_client/grips/protos/shared/live_shopping_event';
import {ElementTrackingContextProvider} from '../../../../../tracking/context/ElementTrackingContext';
import {SectionTrackingContextProvider} from '../../../../../tracking/context/SectionTrackingContext';
import {GenericPromotionTracker} from '../../../../../tracking/trackerComponents/GenericPromotionTracker';
import {darkGrayColor} from '../../../../../ui/constants/colors';
import {
  mediaMinDesktop,
  mediaMinTablet,
} from '../../../../../ui/constants/customMedia';
import {
  fontSize14,
  fontSize20,
  fontSize24,
  fwBold,
  fwMediumBold,
} from '../../../../../ui/constants/typography';
import {EventBanner, LiveEventBanner} from './LiveEventBanner';
import {
  ButtonText,
  LiveEventStartShowButton,
  StyledButton,
} from './LiveEventStartShowButton';

export const LiveEvent: React.FC<{
  event: LiveShoppingEvent;
}> = ({event}) => {
  const {title, teaserImage, subtitle, id, description, state} = event;
  return (
    <SectionTrackingContextProvider>
      <ElementTrackingContextProvider>
        <GenericPromotionTracker
          name={id}
          id={id}
          source={'teaser' as TrackingPromotionCreative}
          url={`/l/live-shopping?autoplayLiveShopping=${id}`}
          resourceId={id}
          resourceType={'liveshoppingPlayer'}
          locationId={'liveshopping_teaser'}
        >
          <LiveShoppingEventContainer
            eventsHaveEnded={state?.$case !== 'ended'}
          >
            <LiveEventBanner event={event} teaserImageUrl={teaserImage?.url} />
            <LiveShoppingDetails>
              <LiveShoppingSubTitle>{subtitle}</LiveShoppingSubTitle>
              <LiveShoppingTitle>{title}</LiveShoppingTitle>
              {description && state?.$case !== 'ended' && (
                <LiveShoppingDesktopDescription>
                  {description}
                </LiveShoppingDesktopDescription>
              )}
              <LiveEventStartShowButton event={event} />
            </LiveShoppingDetails>
          </LiveShoppingEventContainer>
        </GenericPromotionTracker>
      </ElementTrackingContextProvider>
    </SectionTrackingContextProvider>
  );
};

const LiveShoppingDesktopDescription = styled.div`
  margin-bottom: 50px;
  ${fontSize20}
  ${fwMediumBold}
`;

const LiveShoppingDetails = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  padding: 16px 10px;
`;

const LiveShoppingSubTitle = styled.div`
  color: ${darkGrayColor};
  padding-bottom: 7px;
  ${fontSize14};
  ${fwMediumBold};
`;

const LiveShoppingTitle = styled.div`
  padding-bottom: 24px;
  ${fontSize20}
  ${fwBold};
`;
const EVENT_BANNER_CENTER = 390 * 0.5 - 27;
const desktopSectionCss = css`
  ${LiveShoppingDetails} {
    @media ${mediaMinTablet} {
      width: 45%;
      min-width: 300px;
      justify-content: flex-start;
    }

    @media ${mediaMinDesktop} {
      padding: 60px 50px;
    }
  }

  @media ${mediaMinTablet} {
    flex-direction: row;
  }

  ${EventBanner} {
    @media ${mediaMinTablet} {
      height: 530px;
    }
  }
`;

const endSectionDesktopCss = css`
  ${EventBanner} {
    @media ${mediaMinTablet} {
      height: 390px;
    }
  }

  ${LiveShoppingDetails} {
    @media ${mediaMinTablet} {
      padding: 26px;
    }
  }

  ${StyledButton} {
    @media ${mediaMinTablet} {
      position: absolute;
      top: ${EVENT_BANNER_CENTER}px;
      left: 50%;
      margin-left: -27px;
      width: 54px;
      height: 54px;
      padding: 0;
      margin: 0;
      background: rgba(0, 0, 0, 0.3);
      border-radius: 54px;

      span {
        justify-content: center;
      }

      svg {
        width: 35px;
        height: 35px;
        filter: grayscale(1);
      }
    }
  }

  ${LiveShoppingTitle} {
    @media ${mediaMinTablet} {
      padding-bottom: 0;
    }
  }

  ${ButtonText} {
    @media ${mediaMinTablet} {
      display: none;
    }
  }

  @media ${mediaMinTablet} {
    filter: grayscale(1);
    transition: filter 1s;
    margin: 0;
    height: 100%;

    &:hover {
      filter: grayscale(0);
      transition: filter 0.2s;
    }
  }
`;

const LiveShoppingEventContainer = styled.div<{eventsHaveEnded: boolean}>`
  display: flex;
  flex-direction: column;
  margin: 0 15px 20px 15px;
  border-radius: 4px;
  box-shadow: 0 2px 10px 5px rgba(0, 0, 0, 0.08);
  position: relative;

  ${LiveShoppingTitle} {
    @media ${mediaMinTablet} {
      line-height: 1.17;
      ${fontSize24}
    }
  }

  ${props => (props.eventsHaveEnded ? desktopSectionCss : endSectionDesktopCss)}
`;
